import { useCallback, useEffect, useRef, useState } from 'react';

const NFC_HOST = 'local.tomi8.co';

/**
 * Extrae el serial de una URL tipo https://local.tomi8.co?XXXXXXX.
 * Devuelve null si el host no coincide o no hay serial.
 */
export const serialFromNfcUrl = (rawUrl) => {
  try {
    const url = new URL(String(rawUrl || '').trim());
    if (url.hostname.toLowerCase() !== NFC_HOST) return null;
    return decodeURIComponent(url.search.slice(1)).trim() || null;
  } catch (e) {
    return null;
  }
};

const decodeRecord = (record) => {
  if (record.recordType !== 'url' && record.recordType !== 'text') return null;
  try {
    return new TextDecoder(record.encoding || 'utf-8').decode(record.data);
  } catch (e) {
    return null;
  }
};

export const isNfcSupported = () => typeof window !== 'undefined' && 'NDEFReader' in window;

/**
 * Lectura NFC con Web NFC (solo Chrome en Android, HTTPS, página en primer plano).
 * Mientras el escaneo está activo, Chrome entrega el tag a la página y el sistema
 * no abre la URL. La primera activación requiere un gesto del usuario (start()).
 *
 * @param {(serial: string) => void} onSerial se llama al leer un tag con URL local.tomi8.co?<serial>
 * @param {boolean} enabled si es false no se muestra ni se escanea
 */
export default function useNfcReader(onSerial, enabled = true) {
  const supported = enabled && isNfcSupported();
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const controllerRef = useRef(null);
  const onSerialRef = useRef(onSerial);
  onSerialRef.current = onSerial;

  const stop = useCallback(() => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
    setScanning(false);
  }, []);

  const start = useCallback(async () => {
    if (!isNfcSupported() || controllerRef.current) return;
    setError('');
    const controller = new AbortController();
    try {
      const reader = new window.NDEFReader();
      reader.onreadingerror = () => setError('No se pudo leer el tag NFC.');
      reader.onreading = (event) => {
        for (const record of event.message.records) {
          const text = decodeRecord(record);
          const serial = text && serialFromNfcUrl(text);
          if (serial) {
            onSerialRef.current?.(serial);
            return;
          }
        }
        setError('El tag no contiene una URL de local.tomi8.co.');
      };
      await reader.scan({ signal: controller.signal });
      controllerRef.current = controller;
      setScanning(true);
    } catch (e) {
      setError(
        e?.name === 'NotAllowedError'
          ? 'Permiso NFC denegado.'
          : 'No se pudo activar el NFC. Verifica que esté encendido.'
      );
    }
  }, []);

  // Si el permiso ya fue concedido, activa sin pedir gesto; si no, queda el botón.
  useEffect(() => {
    if (!supported) return undefined;
    let cancelled = false;
    navigator.permissions
      ?.query({ name: 'nfc' })
      .then((status) => {
        if (!cancelled && status.state === 'granted') start();
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      stop();
    };
  }, [supported, start, stop]);

  return { supported, scanning, error, start, stop };
}
